import { Subject } from 'rxjs';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Injectable, OnDestroy } from '@angular/core';

import { SnCurrentOrganization } from '@lib/auth';
import { IInnerLayer } from 'apps/dashboard/src/app/map/interfaces/inner-layer.interface';
import {
  SnCamera,
  SnCameraManagementType,
  SnCameraDisposition,
  SnCameraStatus,
  SnCameraUnmanagedType,
} from 'libs/api/entities/src/lib/camera';
import { GoogleMap, GoogleMapLayer } from 'libs/geo/google/map/src';

import { SnVerkadaService } from '../services';
import { SnVerkadaCamera, SnVerkadaPresenceResponse, SnVerkadaStreamResponse } from '../types';
import { VerkadaCameraMarker } from './verkada-camera-marker';

@Injectable({
  providedIn: 'root',
})
export class MapVerkadaService implements OnDestroy, IInnerLayer {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  private _map: GoogleMap;
  private _markers: VerkadaCameraMarker[] = [];
  private readonly _mapLayer: GoogleMapLayer;
  private readonly _destroy$ = new Subject<void>();
  private readonly _disableLayer$ = new Subject<void>();
  private readonly _cameraSelected$ = new Subject<SnCamera>();
  private readonly _cameraRemoved$ = new Subject<SnCamera>();

  get verkadaCloudToCloudEnabled(): boolean {
    return this._currentOrganization.enabledIntegrations.includes('verkadac2c');
  }

  constructor(
    private readonly _currentOrganization: SnCurrentOrganization,
    private readonly _verkadaService: SnVerkadaService,
  ) {
    this._mapLayer = new GoogleMapLayer();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._disableLayer$.next();
    this._disableLayer$.complete();
  }

  public addToMap(): void {
    if (!this.verkadaCloudToCloudEnabled) return; // Only add the layer if the integration is enabled
    this._initLayer();
    this._clearMarkers();
    this._loadAllCameras();
  }

  private _loadAllCameras(pageToken?: string): void {
    this._verkadaService
      .getPresenceData({ ...(pageToken && { pageToken }) })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (res: SnVerkadaPresenceResponse) => {
          // Filter out non-Live cameras and cameras missing location
          const liveCameras = res.cameras.filter( (camera) => {
              return camera.status && camera.status.toLowerCase() === "live" &&
                     camera.locationLat && camera.locationLon
            }
          )
          this._updateMapCameras(liveCameras);
          if (res.nextPageToken) {
            this._loadAllCameras(res.nextPageToken);
          }
        },
        error: (error) => {
          console.error('Error loading Verkada presence data:', error);
        },
      });
  }

  public removeFromMap(): void {
    this._clearMarkers();
    this._mapLayer.remove();
    this._mapLayer.clearOverlays();
  }

  public onMapLoaded(googleMap: GoogleMap): void {
    this._map = googleMap;
  }

  public get cameraSelected$(): Observable<SnCamera> {
    return this._cameraSelected$.asObservable();
  }

  public get cameraRemoved$(): Observable<SnCamera> {
    return this._cameraRemoved$.asObservable();
  }

  public get markers(): VerkadaCameraMarker[] {
    return [ ...this._markers ]; // Return a copy of the markers array, to avoid downstream mutations.
  }

  private _initLayer(): void {
    this._mapLayer.addTo(this._map);
  }

  private _updateMapCameras(cameras: SnVerkadaCamera[]): void {
    cameras.forEach((camera) => {
      const marker = new VerkadaCameraMarker(camera);
      marker.addTo(this._map);
      marker.getMarker().addListener('click', () => {
        this._onCameraClick(marker);
      });
      this._markers.push(marker);
    });
  }

  public _onCameraClick(marker: VerkadaCameraMarker): void {
    const verkadaCamera = marker.camera;
    this._verkadaService
      .getStream({ cameraId: verkadaCamera.cameraId })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (streamResponse: SnVerkadaStreamResponse) => {
            const snCamera = new SnCamera({
              id: verkadaCamera.cameraId,
              name: verkadaCamera.name,
              make: 'verkadac2c',
              model: verkadaCamera.model || 'Unknown',
              isLive: true,
              managementType: SnCameraManagementType.Unmanaged, // External integration cameras are unmanaged
              disposition: SnCameraDisposition.External,
              unmanagedType: SnCameraUnmanagedType.HLS,
              unmanagedParams: { refreshTime: 0 },
              unmanagedUrl: streamResponse.url,
              externalType: 'verkadac2c',
              externalCameraId: verkadaCamera.cameraId,
              loopDuration: 30,
              record: false, // External cameras typically don't record locally.
              liveVideoUrls: { main: { cloud: { hls: streamResponse.url } } },
              streamTokenExpiryTime: streamResponse.expiresAt,
              geometry: {
                type: 'Point',
                coordinates: [verkadaCamera.locationLon, verkadaCamera.locationLat],
              },
              status: SnCameraStatus.Up,
              cameraMeta: { cameraRemoved$: this._cameraRemoved$ }, // Pass the _cameraRemoved$ observable to close the camera stream after the token expires.
            });
            this._cameraSelected$.next(snCamera);
        },
        error: (error) => {
          console.error('Error getting Verkada stream:', error);
        },
      });
  }

  private _clearMarkers(): void {
    this._markers.forEach((marker) => marker.remove());
    this._markers = [];
  }
}
